import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { countries } from '../mock';
import { Upload, Award } from 'lucide-react';

const LionApplication = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    countryOfBirth: '',
    countryOfResidence: '',
    isMinority: '',
    minorityCountry: '',
    howHeard: '',
    occupation: '',
    whyMentor: '',
    experience: '',
    timeCommitment: '',
    numberOfCubs: '',
    passions: '',
    consent: false
  });

  const [resume, setResume] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResume(file);
      toast.success(`Resume uploaded: ${file.name}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error('Please consent to be contacted.');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/applications/lion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      toast.success('Application submitted successfully! Thank you for your interest in mentoring. We will review your application and contact you soon.');
    
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        dateOfBirth: '',
        gender: '',
        countryOfBirth: '',
        countryOfResidence: '',
        isMinority: '',
        minorityCountry: '',
        howHeard: '',
        occupation: '',
        whyMentor: '',
        experience: '',
        timeCommitment: '',
        numberOfCubs: '',
        passions: '',
        consent: false
      });
      setResume(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="min-h-screen flex items-center justify-center text-white relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://www.lionsunleashed.org/wp-content/uploads/2020/07/Contact-Pic.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/90 via-blue-600/85 to-purple-600/90"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10 py-20">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 leading-tight">
              <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }} className="block mb-2">
                Become a
              </span>
              <span className="block font-black">
                <span style={{ color: '#D4AF37' }}>Lion</span> Today
              </span>
            </h1>
            
            <h2 className="text-3xl md:text-5xl font-bold mb-8">
              Share Your Experience & Empower Others
            </h2>
            
            <p className="text-xl md:text-3xl mb-16 leading-relaxed font-light">
              Give a Cub the opportunity of a lifetime! Give back to a community of hardworking individuals and the future generation of professionals.
            </p>
            
            <div className="max-w-4xl mx-auto p-8 bg-white/10 backdrop-blur-md rounded-3xl border border-white/30">
              <p className="text-xl md:text-2xl italic leading-relaxed">
                "The best way to find yourself is to lose yourself in the service of others."
              </p>
              <p className="text-lg md:text-xl mt-4">
                - Mahatma Gandhi
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-2xl border-0">
              <CardHeader className="text-center py-12 bg-gradient-to-br from-white to-amber-50">
                <CardTitle className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                  <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Application</span>{' '}
                  <span className="font-black" style={{ color: '#D4AF37' }}>Form</span>
                </CardTitle>
                <CardDescription className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Please fill out all required fields to complete your application. We review all applications carefully and will contact you within 2 weeks.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Personal Information</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input 
                        id="firstName" 
                        name="firstName" 
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input 
                        id="lastName" 
                        name="lastName" 
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        value={formData.email}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input 
                        id="phone" 
                        name="phone" 
                        type="tel" 
                        value={formData.phone}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">Address *</Label>
                    <Input 
                      id="address" 
                      name="address" 
                      value={formData.address}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                      <Input 
                        id="dateOfBirth" 
                        name="dateOfBirth" 
                        type="date" 
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        required 
                      />
                    </div>
                    <div>
                      <Label>Gender *</Label>
                      <RadioGroup 
                        value={formData.gender}
                        onValueChange={(value) => handleSelectChange('gender', value)}
                        className="flex flex-wrap gap-4 mt-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="male" id="male" />
                          <Label htmlFor="male">Male</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="female" id="female" />
                          <Label htmlFor="female">Female</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="other" id="other" />
                          <Label htmlFor="other">Other</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="prefer-not-to-say" id="prefer-not" />
                          <Label htmlFor="prefer-not">Prefer Not To Say</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                {/* Background Information */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Background Information</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="countryOfBirth">What is your country of birth? *</Label>
                      <Select value={formData.countryOfBirth} onValueChange={(value) => handleSelectChange('countryOfBirth', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="countryOfResidence">What is your country of residence? *</Label>
                      <Select value={formData.countryOfResidence} onValueChange={(value) => handleSelectChange('countryOfResidence', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Are you a racial or ethnic minority? *</Label>
                    <RadioGroup 
                      value={formData.isMinority}
                      onValueChange={(value) => handleSelectChange('isMinority', value)}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="minority-yes" />
                        <Label htmlFor="minority-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="minority-no" />
                        <Label htmlFor="minority-no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {formData.isMinority === 'yes' && (
                    <div>
                      <Label>If yes, what country?</Label>
                      <Select value={formData.minorityCountry} onValueChange={(value) => handleSelectChange('minorityCountry', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Mentorship Information */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Mentorship Information</h3>
                  
                  <div>
                    <Label htmlFor="howHeard">How did you hear about our program? Were you referred by someone on our team? If so, who? *</Label>
                    <Input 
                      id="howHeard" 
                      name="howHeard" 
                      value={formData.howHeard}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="occupation">What is your occupation? *</Label>
                    <Input 
                      id="occupation" 
                      name="occupation" 
                      value={formData.occupation}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="whyMentor">Why do you want to be a mentor and why would CUBS benefit from having you as a mentor? *</Label>
                    <Textarea 
                      id="whyMentor" 
                      name="whyMentor" 
                      value={formData.whyMentor}
                      onChange={handleInputChange}
                      rows={5}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="experience">Describe your experience/any hardships you faced getting to where you are now. (ex. Childhood, getting into/during college, getting/during your first job) *</Label>
                    <Textarea 
                      id="experience" 
                      name="experience" 
                      value={formData.experience}
                      onChange={handleInputChange}
                      rows={5}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="timeCommitment">How much time would you be willing to give to the program per month? *</Label>
                    <Input 
                      id="timeCommitment" 
                      name="timeCommitment" 
                      value={formData.timeCommitment}
                      onChange={handleInputChange}
                      placeholder="e.g., 5-10 hours"
                      required
                    />
                  </div>

                  <div>
                    <Label>How many CUBS would you be willing to mentor at once? *</Label>
                    <RadioGroup 
                      value={formData.numberOfCubs}
                      onValueChange={(value) => handleSelectChange('numberOfCubs', value)}
                      className="flex flex-wrap gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1" id="cubs-1" />
                        <Label htmlFor="cubs-1">1</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="2-5" id="cubs-2-5" />
                        <Label htmlFor="cubs-2-5">2-5</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="other" id="cubs-other" />
                        <Label htmlFor="cubs-other">Other</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label htmlFor="passions">What are you passionate about? *</Label>
                    <Textarea 
                      id="passions" 
                      name="passions" 
                      value={formData.passions}
                      onChange={handleInputChange}
                      rows={4}
                      required
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">Additional Materials</h3>
                  
                  <div>
                    <Label htmlFor="resume">Upload Resume *</Label>
                    <div className="mt-2">
                      <label 
                        htmlFor="resume" 
                        className="flex items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-lg appearance-none cursor-pointer hover:border-cyan-500 focus:outline-none"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <Upload className="w-8 h-8 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {resume ? resume.name : 'Click to upload resume (PDF, DOC, DOCX)'}
                          </span>
                        </div>
                        <input 
                          id="resume" 
                          type="file" 
                          className="hidden" 
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Consent */}
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="consent" 
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                  />
                  <Label htmlFor="consent" className="text-sm">
                    I consent to be contacted by a representative of Lions Unleashed. *
                  </Label>
                </div>

                {/* Submit Button */}
                <div className="flex gap-4">
                  <Button 
                    type="submit" 
                    size="lg"
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg py-6"
                  >
                    Submit Application
                  </Button>
                </div>

                <p className="text-sm text-gray-600 text-center italic">
                  *Application subject to approval. We will review your application and contact you within 2 weeks.
                </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LionApplication;